import "./pdf-signer.css";
import { pdfjs } from "./pdfjs-adapter.js";
import SignaturePad from "signature_pad";
import latinFontUrl from "@fontsource/noto-sans/files/noto-sans-latin-ext-400-normal.woff2";
import georgianFontUrl from "@fontsource/noto-sans-georgian/files/noto-sans-georgian-georgian-400-normal.woff2";
import {
  clampAnnotation,
  formatDate,
  hasPdfHeader,
  outputFileName,
  todayIso,
  validateFileMetadata,
  validatePageCount
} from "./pdf-core.js";
import { exportAnnotatedPdf } from "./pdf-export.js";
import { createOperationResult } from "./operation-result.js";

const root = document.querySelector("[data-pdf-signer]");

if (root) {
  const formMode = root.dataset.signerMode === "form";
  const elements = Object.fromEntries([
    "statusMessage", "uploadPanel", "dropZone", "chooseFileButton", "pdfFileInput", "editorPanel",
    "fileName", "fileMeta", "replaceButton", "downloadButton", "thumbnailList", "previousPage",
    "nextPage", "currentPageLabel", "pageCountLabel", "zoomOut", "zoomIn", "zoomLabel", "pageStage",
    "pageSurface", "pdfCanvas", "annotationLayer", "emptyProperties", "annotationProperties", "valueField",
    "valueLabel", "annotationValue", "dateFormatField", "dateFormat", "fontSizeField", "fontSize",
    "fontSizeOutput", "deleteAnnotation", "signatureDialog", "signatureEyebrow", "signatureDialogTitle", "flattenFormFields",
    "savedSignaturesPanel", "savedSignaturesList", "drawSignaturePanel", "drawSignatureHelp", "initialsPanel",
    "signatureCanvas", "clearSignature", "initialsInput", "initialsPreview", "saveSignature"
  ].map(id => [id, document.getElementById(id)]));

  const state = {
    file: null,
    originalBytes: null,
    pdf: null,
    pageNumber: 1,
    zoom: 1,
    annotations: [],
    savedSignatures: [],
    selectedId: null,
    currentViewport: null,
    currentRenderTask: null,
    nextId: 1,
    nextSavedSignatureId: 1,
    assetMode: "signature",
    busy: false,
    nativeFields: new Map(),
    formValues: new Map()
  };
  const operationResult = createOperationResult(root, elements.editorPanel);

  const signaturePad = new SignaturePad(elements.signatureCanvas, {
    minWidth: 1,
    maxWidth: 3.2,
    penColor: "rgb(17, 24, 39)"
  });

  function showStatus(message, kind = "error") {
    elements.statusMessage.textContent = message;
    elements.statusMessage.hidden = !message;
    elements.statusMessage.dataset.kind = kind;
    if (message) elements.statusMessage.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function clearStatus() {
    elements.statusMessage.hidden = true;
    elements.statusMessage.textContent = "";
  }

  function setBusy(busy, message = "") {
    state.busy = busy;
    elements.downloadButton.disabled = busy;
    elements.replaceButton.disabled = busy;
    elements.downloadButton.textContent = busy ? message || "Working…" : "Finish PDF";
  }

  async function loadFile(file) {
    const metadataError = validateFileMetadata(file);
    if (metadataError) return showStatus(metadataError);

    setBusy(true, "Opening…");
    clearStatus();
    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      if (!hasPdfHeader(bytes.slice(0, 5))) throw new Error("The selected file does not have a valid PDF header.");

      const loadingTask = pdfjs.getDocument({ data: bytes.slice() });
      const pdf = await loadingTask.promise;
      const pageError = validatePageCount(pdf.numPages);
      if (pageError) {
        await pdf.destroy();
        throw new Error(pageError);
      }

      if (state.pdf) await state.pdf.destroy();
      state.file = file;
      state.originalBytes = bytes;
      state.pdf = pdf;
      state.pageNumber = 1;
      state.zoom = 1;
      state.annotations = [];
      state.savedSignatures = [];
      state.selectedId = null;
      state.nextId = 1;
      state.nextSavedSignatureId = 1;
      state.nativeFields = new Map();
      state.formValues = new Map();

      elements.fileName.textContent = file.name;
      elements.fileMeta.textContent = `${formatBytes(file.size)} · ${pdf.numPages} page${pdf.numPages === 1 ? "" : "s"}`;
      elements.pageCountLabel.textContent = String(pdf.numPages);
      elements.uploadPanel.hidden = true;
      elements.editorPanel.hidden = false;
      root.classList.add("is-editing");
      document.body.classList.add("pdf-editor-active");
      updateProperties();
      await renderPage();
      void renderThumbnails();
    } catch (error) {
      const message = error?.name === "PasswordException"
        ? "Password-protected PDFs are not supported in this version."
        : error?.message || "smallpdf.app could not open this PDF.";
      showStatus(message);
    } finally {
      setBusy(false);
      elements.pdfFileInput.value = "";
    }
  }

  function formatBytes(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function renderPage() {
    if (!state.pdf) return;
    if (state.currentRenderTask) {
      try { state.currentRenderTask.cancel(); } catch { /* already complete */ }
    }

    const page = await state.pdf.getPage(state.pageNumber);
    const natural = page.getViewport({ scale: 1 });
    const availableWidth = Math.max(320, elements.pageStage.clientWidth - 64);
    const fitScale = Math.min(1.35, availableWidth / natural.width);
    const scale = fitScale * state.zoom;
    const viewport = page.getViewport({ scale });
    state.currentViewport = viewport;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = elements.pdfCanvas;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    elements.pageSurface.style.width = `${viewport.width}px`;
    elements.pageSurface.style.height = `${viewport.height}px`;

    const context = canvas.getContext("2d", { alpha: false });
    state.currentRenderTask = page.render({
      canvasContext: context,
      viewport,
      transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0]
    });
    try {
      await state.currentRenderTask.promise;
    } catch (error) {
      if (error?.name !== "RenderingCancelledException") throw error;
      return;
    }

    elements.currentPageLabel.textContent = String(state.pageNumber);
    elements.zoomLabel.textContent = `${Math.round(state.zoom * 100)}%`;
    elements.previousPage.disabled = state.pageNumber === 1;
    elements.nextPage.disabled = state.pageNumber === state.pdf.numPages;
    elements.zoomOut.disabled = state.zoom <= 0.6;
    elements.zoomIn.disabled = state.zoom >= 2;
    updateCurrentThumbnail();
    if (formMode && !state.nativeFields.has(state.pageNumber)) {
      const annotations = await page.getAnnotations({ intent: "display" });
      state.nativeFields.set(state.pageNumber, Array.from(annotations || []).filter(annotation => annotation.subtype === "Widget" && annotation.fieldName));
    }
    renderAnnotations();
  }

  async function renderThumbnails() {
    elements.thumbnailList.replaceChildren();
    for (let pageNumber = 1; pageNumber <= state.pdf.numPages; pageNumber += 1) {
      if (!state.pdf) return;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "thumbnail-button";
      button.dataset.page = String(pageNumber);
      button.setAttribute("aria-label", `Open page ${pageNumber}`);
      const canvas = document.createElement("canvas");
      const label = document.createElement("span");
      label.textContent = String(pageNumber);
      button.append(canvas, label);
      button.addEventListener("click", () => goToPage(pageNumber));
      elements.thumbnailList.append(button);

      const page = await state.pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 0.18 });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    }
    updateCurrentThumbnail();
  }

  async function goToPage(pageNumber) {
    if (!state.pdf || pageNumber < 1 || pageNumber > state.pdf.numPages || pageNumber === state.pageNumber) return;
    state.pageNumber = pageNumber;
    state.selectedId = null;
    updateProperties();
    await renderPage();
  }

  function updateCurrentThumbnail() {
    elements.thumbnailList.querySelectorAll(".thumbnail-button").forEach(button => {
      const active = Number(button.dataset.page) === state.pageNumber;
      if (active) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    });
  }

  function currentAnnotations() {
    return state.annotations.filter(annotation => annotation.page === state.pageNumber);
  }

  function renderAnnotations() {
    elements.annotationLayer.replaceChildren();
    if (formMode) renderNativeFields();
    for (const annotation of currentAnnotations()) {
      const item = document.createElement("div");
      item.className = `annotation${annotation.id === state.selectedId ? " is-selected" : ""}`;
      item.dataset.id = annotation.id;
      item.tabIndex = 0;
      item.setAttribute("role", "button");
      item.setAttribute("aria-label", `${annotation.type} annotation`);
      applyAnnotationStyle(item, annotation);

      if (annotation.type === "signature" || annotation.type === "initials") {
        const image = document.createElement("img");
        image.src = annotation.image;
        image.alt = "";
        item.append(image);
      } else {
        const text = document.createElement("span");
        text.className = "annotation-text";
        text.style.fontSize = `${annotation.fontSize * (state.currentViewport?.scale || 1)}px`;
        text.textContent = annotation.type === "date" ? formatDate(annotation.value, annotation.dateFormat) : annotation.type === "checkmark" ? "✓" : annotation.value;
        item.append(text);
      }

      for (const handle of ["nw", "ne", "sw", "se"]) {
        const resize = document.createElement("span");
        resize.className = "resize-handle";
        resize.dataset.handle = handle;
        resize.setAttribute("aria-hidden", "true");
        item.append(resize);
      }

      item.addEventListener("pointerdown", startInteraction);
      item.addEventListener("focus", () => selectAnnotation(annotation.id));
      elements.annotationLayer.append(item);
    }
  }

  function renderNativeFields() {
    const fields = state.nativeFields.get(state.pageNumber) || [];
    const viewport = state.currentViewport;
    if (!viewport) return;
    for (const field of fields) {
      const [left, top, right, bottom] = viewport.convertToViewportRectangle(field.rect);
      const x = Math.min(left, right); const y = Math.min(top, bottom);
      const width = Math.max(12, Math.abs(right - left)); const height = Math.max(12, Math.abs(bottom - top));
      const saved = state.formValues.get(field.fieldName);
      let control;
      if (field.fieldType === "Tx") {
        control = document.createElement("input"); control.type = "text";
        control.value = saved?.value ?? field.fieldValue ?? "";
        control.addEventListener("input", () => state.formValues.set(field.fieldName, { type: "text", value: control.value }));
      } else if (field.fieldType === "Ch") {
        control = document.createElement("select");
        for (const option of Array.from(field.options || [])) {
          const item = document.createElement("option");
          item.value = option.exportValue ?? option.displayValue ?? String(option); item.textContent = option.displayValue ?? option.exportValue ?? String(option); control.append(item);
        }
        control.value = saved?.value ?? field.fieldValue ?? "";
        control.addEventListener("change", () => state.formValues.set(field.fieldName, { type: "choice", value: control.value }));
      } else if (field.fieldType === "Btn" && (field.checkBox || field.radioButton)) {
        const label = document.createElement("label"); label.className = "native-form-label";
        control = document.createElement("input"); control.type = field.radioButton ? "radio" : "checkbox"; control.name = field.fieldName;
        const optionValue = field.buttonValue || field.exportValue || "Yes";
        control.checked = field.radioButton ? (saved?.value ?? field.fieldValue) === optionValue : Boolean(saved?.value ?? (field.fieldValue && field.fieldValue !== "Off"));
        control.addEventListener("change", () => state.formValues.set(field.fieldName, { type: field.radioButton ? "radio" : "checkbox", value: field.radioButton ? optionValue : control.checked }));
        label.append(control); control = label;
      } else continue;
      control.classList.add("native-form-field");
      control.style.left = `${x}px`; control.style.top = `${y}px`; control.style.width = `${width}px`; control.style.height = `${height}px`;
      control.setAttribute("aria-label", field.alternativeText || field.fieldName);
      control.addEventListener("pointerdown", event => event.stopPropagation());
      elements.annotationLayer.append(control);
    }
  }

  function applyAnnotationStyle(item, annotation) {
    item.style.left = `${annotation.x * 100}%`;
    item.style.top = `${annotation.y * 100}%`;
    item.style.width = `${annotation.width * 100}%`;
    item.style.height = `${annotation.height * 100}%`;
  }

  function startInteraction(event) {
    event.preventDefault();
    event.stopPropagation();
    const item = event.currentTarget;
    const annotation = state.annotations.find(value => value.id === item.dataset.id);
    if (!annotation) return;
    selectAnnotation(annotation.id);

    const handle = event.target.dataset.handle;
    const start = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      annotationX: annotation.x,
      annotationY: annotation.y,
      width: annotation.width,
      height: annotation.height
    };
    const bounds = elements.annotationLayer.getBoundingClientRect();
    item.setPointerCapture(event.pointerId);

    const move = moveEvent => {
      const dx = (moveEvent.clientX - start.pointerX) / bounds.width;
      const dy = (moveEvent.clientY - start.pointerY) / bounds.height;
      let candidate = { ...annotation };
      if (!handle) {
        candidate.x = start.annotationX + dx;
        candidate.y = start.annotationY + dy;
      } else {
        if (handle.includes("e")) candidate.width = start.width + dx;
        if (handle.includes("s")) candidate.height = start.height + dy;
        if (handle.includes("w")) { candidate.x = start.annotationX + dx; candidate.width = start.width - dx; }
        if (handle.includes("n")) { candidate.y = start.annotationY + dy; candidate.height = start.height - dy; }
      }
      Object.assign(annotation, clampAnnotation(candidate));
      applyAnnotationStyle(item, annotation);
    };

    const end = () => {
      item.removeEventListener("pointermove", move);
      item.removeEventListener("pointerup", end);
      item.removeEventListener("pointercancel", end);
      renderAnnotations();
    };
    item.addEventListener("pointermove", move);
    item.addEventListener("pointerup", end);
    item.addEventListener("pointercancel", end);
  }

  function selectAnnotation(id) {
    if (state.selectedId === id) return;
    state.selectedId = id;
    renderAnnotations();
    updateProperties();
  }

  function addAnnotation(type, data = {}) {
    const defaults = type === "signature"
      ? { width: 0.3, height: 0.1 }
      : type === "initials"
        ? { width: 0.18, height: 0.085 }
        : type === "checkmark"
          ? { width: 0.06, height: 0.06 }
        : { width: 0.25, height: 0.06 };
    const annotation = clampAnnotation({
      id: `annotation-${state.nextId++}`,
      type,
      page: state.pageNumber,
      x: (1 - defaults.width) / 2,
      y: (1 - defaults.height) / 2,
      ...defaults,
      fontSize: 18,
      ...data
    });
    state.annotations.push(annotation);
    state.selectedId = annotation.id;
    renderAnnotations();
    updateProperties();
  }

  function deleteSelected() {
    if (!state.selectedId) return;
    state.annotations = state.annotations.filter(annotation => annotation.id !== state.selectedId);
    state.selectedId = null;
    renderAnnotations();
    updateProperties();
  }

  function updateProperties() {
    const annotation = state.annotations.find(value => value.id === state.selectedId);
    elements.emptyProperties.hidden = Boolean(annotation);
    elements.annotationProperties.hidden = !annotation;
    if (!annotation) return;

    const isText = annotation.type === "text" || annotation.type === "date" || annotation.type === "checkmark";
    elements.valueField.hidden = !isText;
    elements.fontSizeField.hidden = !isText;
    elements.dateFormatField.hidden = annotation.type !== "date";
    if (isText) {
      elements.valueLabel.textContent = annotation.type === "date" ? "Date" : annotation.type === "checkmark" ? "Checkmark" : "Text";
      elements.annotationValue.type = annotation.type === "date" ? "date" : "text";
      elements.annotationValue.value = annotation.value;
      elements.annotationValue.disabled = annotation.type === "checkmark";
      elements.fontSize.value = String(annotation.fontSize);
      elements.fontSizeOutput.value = String(annotation.fontSize);
      if (annotation.type === "date") elements.dateFormat.value = annotation.dateFormat;
    }
  }

  function openAssetDialog(type) {
    state.assetMode = type;
    const isInitials = type === "initials";
    elements.signatureEyebrow.textContent = isInitials ? "Initials" : "Signature";
    elements.signatureDialogTitle.textContent = isInitials ? "Create your initials" : "Add a signature";
    elements.saveSignature.textContent = isInitials ? "Add to PDF" : "Save and add";
    elements.drawSignatureHelp.textContent = state.savedSignatures.length
      ? "Or draw a new signature using your mouse or finger."
      : "Draw inside the box using your mouse or finger.";
    elements.drawSignaturePanel.hidden = isInitials;
    elements.initialsPanel.hidden = !isInitials;
    renderSavedSignatures(isInitials);
    elements.signatureDialog.showModal();
    if (isInitials) {
      elements.initialsInput.focus();
      updateInitialsPreview();
    } else {
      requestAnimationFrame(resizeSignatureCanvas);
    }
  }

  function renderSavedSignatures(isInitials = state.assetMode === "initials") {
    elements.savedSignaturesList.replaceChildren();
    elements.savedSignaturesPanel.hidden = isInitials || state.savedSignatures.length === 0;
    if (elements.savedSignaturesPanel.hidden) return;

    state.savedSignatures.forEach((signature, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "saved-signature-button";
      button.setAttribute("aria-label", `Use saved signature ${index + 1}`);
      const image = document.createElement("img");
      image.src = signature.image;
      image.alt = "";
      button.append(image);
      button.addEventListener("click", () => {
        addAnnotation("signature", { image: signature.image });
        clearStatus();
        elements.signatureDialog.close();
      });
      elements.savedSignaturesList.append(button);
    });
  }

  function resizeSignatureCanvas() {
    const canvas = elements.signatureCanvas;
    const bounds = canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(bounds.width * ratio));
    canvas.height = Math.max(1, Math.floor(bounds.height * ratio));
    canvas.getContext("2d").scale(ratio, ratio);
    signaturePad.clear();
  }

  function updateInitialsPreview() {
    elements.initialsPreview.textContent = elements.initialsInput.value.trim() || "AB";
  }

  async function initialsImage(value) {
    await document.fonts.ready;
    const measuringCanvas = document.createElement("canvas");
    const measuringContext = measuringCanvas.getContext("2d");
    let fontSize = 190;
    measuringContext.font = `600 ${fontSize}px Caveat, 'Noto Sans', sans-serif`;
    const measuredWidth = measuringContext.measureText(value).width;
    const canvasWidth = Math.max(360, Math.min(1600, Math.ceil(measuredWidth + 80)));
    if (measuredWidth > canvasWidth - 80) {
      fontSize = Math.max(18, fontSize * (canvasWidth - 80) / measuredWidth);
    }

    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = 260;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#111827";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = `600 ${fontSize}px Caveat, 'Noto Sans', sans-serif`;
    context.fillText(value, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
  }

  async function saveAsset() {
    if (state.assetMode === "signature") {
      if (signaturePad.isEmpty()) return showStatus("Draw a signature before adding it to the PDF.");
      const image = signaturePad.toDataURL("image/png");
      state.savedSignatures.push({ id: `signature-${state.nextSavedSignatureId++}`, image });
      addAnnotation("signature", { image });
    } else {
      const value = elements.initialsInput.value.trim();
      if (!value) return showStatus("Enter your initials before adding them to the PDF.");
      addAnnotation("initials", {
        image: await initialsImage(value),
        value,
        width: Math.min(.72, Math.max(.18, value.length * .035))
      });
    }
    clearStatus();
    elements.signatureDialog.close();
  }

  async function downloadPdf() {
    if (!state.originalBytes || state.busy) return;
    setBusy(true, "Generating…");
    clearStatus();
    try {
      const [latinBytes, georgianBytes] = await Promise.all([
        fetch(latinFontUrl).then(response => response.arrayBuffer()),
        fetch(georgianFontUrl).then(response => response.arrayBuffer())
      ]);
      const output = await exportAnnotatedPdf(state.originalBytes, state.annotations, { latin: latinBytes, georgian: georgianBytes }, {
        formValues: state.formValues,
        flattenForm: Boolean(elements.flattenFormFields?.checked)
      });
      const blob = new Blob([output], { type: "application/pdf" });
      operationResult.show({
        blob,
        fileName: outputFileName(state.file.name, formMode ? "filled" : "signed"),
        message: formMode ? "Your filled PDF was created successfully." : "Your signed PDF was created successfully.",
        details: [`${state.pdf.numPages} ${state.pdf.numPages === 1 ? "page" : "pages"}`]
      });
    } catch (error) {
      showStatus(error?.message || "smallpdf.app could not generate the signed PDF.");
    } finally {
      setBusy(false);
    }
  }

  elements.chooseFileButton.addEventListener("click", event => { event.stopPropagation(); elements.pdfFileInput.click(); });
  elements.pdfFileInput.addEventListener("change", () => loadFile(elements.pdfFileInput.files[0]));
  elements.dropZone.addEventListener("click", event => { if (event.target !== elements.chooseFileButton) elements.pdfFileInput.click(); });
  elements.dropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.pdfFileInput.click(); }
  });
  for (const eventName of ["dragenter", "dragover"]) {
    elements.dropZone.addEventListener(eventName, event => { event.preventDefault(); elements.dropZone.classList.add("is-dragging"); });
  }
  for (const eventName of ["dragleave", "drop"]) {
    elements.dropZone.addEventListener(eventName, event => { event.preventDefault(); elements.dropZone.classList.remove("is-dragging"); });
  }
  elements.dropZone.addEventListener("drop", event => loadFile(event.dataTransfer.files[0]));
  elements.replaceButton.addEventListener("click", () => {
    if (state.annotations.length && !window.confirm("Replace this PDF and discard all current annotations?")) return;
    elements.pdfFileInput.click();
  });
  document.querySelectorAll("[data-back-to-tools]").forEach(link => link.addEventListener("click", event => {
    if (state.annotations.length && !window.confirm("Leave the editor and discard all current annotations?")) {
      event.preventDefault();
    }
  }));
  elements.downloadButton.addEventListener("click", downloadPdf);
  elements.previousPage.addEventListener("click", () => goToPage(state.pageNumber - 1));
  elements.nextPage.addEventListener("click", () => goToPage(state.pageNumber + 1));
  elements.zoomOut.addEventListener("click", async () => { state.zoom = Math.max(.6, state.zoom - .2); await renderPage(); });
  elements.zoomIn.addEventListener("click", async () => { state.zoom = Math.min(2, state.zoom + .2); await renderPage(); });
  elements.annotationLayer.addEventListener("pointerdown", event => {
    if (event.target === elements.annotationLayer) { state.selectedId = null; renderAnnotations(); updateProperties(); }
  });
  document.querySelectorAll("[data-add]").forEach(button => button.addEventListener("click", () => {
    const type = button.dataset.add;
    if (type === "signature" || type === "initials") openAssetDialog(type);
    if (type === "text") addAnnotation("text", { value: "Type text" });
    if (type === "date") addAnnotation("date", { value: todayIso(), dateFormat: "DMY" });
    if (type === "checkmark") addAnnotation("checkmark", { value: "✓", fontSize: 22 });
  }));
  elements.annotationValue.addEventListener("input", () => {
    const annotation = state.annotations.find(value => value.id === state.selectedId);
    if (!annotation) return;
    annotation.value = elements.annotationValue.value;
    renderAnnotations();
  });
  elements.dateFormat.addEventListener("change", () => {
    const annotation = state.annotations.find(value => value.id === state.selectedId);
    if (!annotation) return;
    annotation.dateFormat = elements.dateFormat.value;
    renderAnnotations();
  });
  elements.fontSize.addEventListener("input", () => {
    const annotation = state.annotations.find(value => value.id === state.selectedId);
    if (!annotation) return;
    annotation.fontSize = Number(elements.fontSize.value);
    elements.fontSizeOutput.value = elements.fontSize.value;
    renderAnnotations();
  });
  elements.deleteAnnotation.addEventListener("click", deleteSelected);
  elements.clearSignature.addEventListener("click", () => signaturePad.clear());
  elements.initialsInput.addEventListener("input", updateInitialsPreview);
  elements.saveSignature.addEventListener("click", saveAsset);
  document.addEventListener("keydown", event => {
    if ((event.key === "Delete" || event.key === "Backspace") && state.selectedId && !["INPUT", "SELECT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
      event.preventDefault();
      deleteSelected();
    }
  });

  let resizeTimer;
  window.addEventListener("resize", () => {
    if (!state.pdf) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(renderPage, 150);
  });
  window.addEventListener("beforeunload", event => {
    if (!state.annotations.length || operationResult.isVisible) return;
    event.preventDefault();
    event.returnValue = "";
  });
  window.addEventListener("pagehide", () => { operationResult.dispose(); state.pdf?.destroy(); });
}
